"use client";

import { signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import Image from "next/image";
import { useMemo, useState } from "react";
import { LuEllipsisVertical as LuMoreVertical, LuShare } from "react-icons/lu";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { APP_NAME_GENERIC } from "@/utils/constants";
import { cn } from "@/utils/lib";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deferredPromptSignal = signal<any>(null);

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    console.log("beforeinstallprompt event triggered");
    // Prevent the default behavior
    event.preventDefault();
    // Stash the event in the signal
    deferredPromptSignal.value = event;
  });
}

interface PWAInstallModalProps {
  onCancel: () => void;
  onSkip: () => void;
}

export function PWAInstallModal({ onCancel, onSkip }: PWAInstallModalProps) {
  const [isOpen, setIsOpen] = useState(true);
  const isSafari = useMemo(
    () =>
      typeof navigator !== "undefined" &&
      /^((?!chrome|android|CriOS).)*safari/i.test(navigator.userAgent),
    [],
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(x) => {
        if (!x) onCancel();

        setIsOpen(x);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-4">
            <Image
              src="/assets/app-icon.png"
              alt="PWA Install Modal"
              width={44}
              height={44}
            />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-muted-foreground">
                Install {APP_NAME_GENERIC} App
              </span>
              <span className="text-sm text-foreground">
                Add to Home Screen
              </span>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          <div className="p-6 pb-4 text-sm text-foreground">
            {isSafari ? (
              <div className="flex flex-col gap-2">
                <div className="mb-2 flex items-start gap-4 text-left">
                  <div
                    className="
                      flex items-center justify-center rounded-full border
                      border-primary bg-background p-2
                    "
                  >
                    <LuShare className="text-2xl text-primary" />
                  </div>
                  <div>
                    1. Tap the{" "}
                    <span className="font-semibold text-primary">Share</span>{" "}
                    button below
                    <br />
                    2. Select{" "}
                    <span className="font-semibold text-primary">
                      &ldquo;Add to Home Screen&rdquo;
                    </span>
                  </div>
                </div>
                <Image
                  src="/assets/app-pwa-ios.png"
                  alt="PWA Install Modal"
                  className="w-full"
                  width={300}
                  height={0}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="mb-2 flex items-start gap-4 text-left">
                  <div
                    className="
                      flex items-center justify-center rounded-full border
                      border-primary bg-background p-2
                    "
                  >
                    <LuMoreVertical className="text-2xl text-primary" />
                  </div>
                  <div>
                    1. Tap the{" "}
                    <span className="font-semibold text-primary">Menu</span>{" "}
                    icon by the address bar
                    <br />
                    2. Select{" "}
                    <span className="font-semibold text-primary">
                      &ldquo;Add to Home Screen&rdquo;
                    </span>
                  </div>
                </div>
                <Image
                  src="/assets/app-pwa-android.png"
                  alt="PWA Install Modal"
                  className="w-full rounded-2xl"
                  width={300}
                  height={0}
                />
              </div>
            )}
          </div>
          <DialogPortal>
            <div
              className={cn("pointer-events-none fixed z-100 mx-auto", {
                "bottom-[80px] left-[47%] translate-y-1/2": isSafari,
                "top-[-40px] right-[12%] translate-y-1/2 rotate-180": !isSafari,
              })}
            >
              <svg
                width="45"
                height="119"
                viewBox="0 0 45 119"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M41.9623 0.226546C41.9154 0.320411 41.329 1.82224 40.6723 3.55873C40.039 5.29523 38.1628 9.73034 36.5444 13.4145C31.6425 24.4436 29.8835 28.8084 24.0669 44.2726C19.8921 55.3486 13.4187 75.7642 8.72794 92.6129L7.2269 97.9867L7.08616 94.8422C6.82816 89.3511 5.89001 87.333 3.52116 87.333C2.51263 87.333 1.71519 88.1544 1.45719 89.4919C1.33992 90.0551 1.08195 93.0823 0.894318 96.2502C0.706686 99.4181 0.448692 103.572 0.307967 105.519C0.190697 107.444 0.0499847 110.682 0.0265307 112.677C-0.0438314 115.985 0.00303082 116.384 0.448657 117.135C1.43373 118.825 3.28661 119.435 5.09257 118.684C6.03073 118.285 6.94542 117.206 9.713 113.263C13.9816 107.185 18.907 101.272 23.1991 97.0481C28.0775 92.2609 28.3355 91.7212 25.0754 93.1996C21.0647 94.983 16.6085 97.9867 12.7386 101.483C11.519 102.586 10.487 103.454 10.4401 103.407C10.3463 103.314 13.8409 91.0172 17.101 80.0585C19.7513 71.1883 22.7065 61.6141 24.747 55.419C27.5615 46.8069 34.6681 27.3065 38.2331 18.4598C41.7043 9.82421 43.4399 4.96671 43.9325 2.54969C44.167 1.37638 44.1435 1.11826 43.8386 0.602C43.4633 0.038811 42.2437 -0.219311 41.9623 0.226546Z"
                  fill="red"
                />
              </svg>
            </div>
          </DialogPortal>
          <div
            className={cn("flex w-full gap-2 border-t bg-card p-4", {
              "justify-end": !isSafari,
              "justify-between": isSafari,
            })}
          >
            <InstallPrompt />
            <Button variant="outline" onClick={onCancel}>
              Back
            </Button>
            <Button variant="outline" onClick={onSkip}>
              Skip
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const InstallPrompt = () => {
  useSignals();

  const deferredPrompt = deferredPromptSignal.value;

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();

      const choiceResult = await deferredPrompt.userChoice;

      if (choiceResult.outcome === "accepted") {
        console.log("User accepted the install prompt");
      } else {
        console.log("User dismissed the install prompt");
      }

      deferredPromptSignal.value = null;
    }
  };

  if (!deferredPrompt) {
    return null;
  }

  return (
    <Button variant="default" onClick={handleInstallClick}>
      Install App
    </Button>
  );
};
